import { InlineLoadingSpinner } from '@/components/common/LoadingSpinner'
import CollapsibleCard from '@/components/shared/CollapsibleCard'
import NavMenu from '@/components/shared/NavMenu'
import MedReqStatusBadge from '@/components/shared/MedReqStatusBadge'
import OrderStatusBadge from '@/components/shared/OrderStatusBadge'
import OrderSubscriptionTypeChip from '@/components/shared/OrderSubscriptionTypeChip'
import BackButton from '@/components/ui/BackButton'
import { DetailField, DetailFieldRow } from '@/components/ui/DetailField'
import TabButton from '@/components/ui/TabButton'
import { orderStatusValueToLabel } from '@/constants/statuses'
import type { OrderSubscriptionType } from '@/types/order'
import { formatDateTime, scrollToSection } from '@/lib/utils'
import { format } from 'date-fns'
import { isReadOnly } from '@/lib/permissions'
import { formatFrequency } from '@/lib/subscriptions'
import { useReviewQuestionnaire, useSendReviewQuestionnaire } from '@/services/reviewQuestionnaire'
import { useSubscriptionByHashQuery, type Subscription } from '@/services/subscriptions'
import { borderRadius, colors, sxPresets } from '@/theme'
import { Box, Grid, Paper, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import CancelSubscriptionDialog from './sections/CancelSubscriptionDialog'

const CANCELLABLE_SUBSCRIPTION_STATUSES = ['wc-active', 'wc-on-hold', 'wc-pending']

const navItems = [
  { id: 'subscription-status', title: 'Subscription Status' },
  { id: 'subscription-details', title: 'Subscription Details' },
  { id: 'medications', title: 'Medications' },
  { id: 'subscription-orders', title: 'Orders' }
]

const sectionStyles = {
  p: 3,
  borderRadius: borderRadius.lg,
  border: `1px solid ${colors.border.default}`
}

function formatDate(value: string): string {
  return format(new Date(value), 'dd/MM/yyyy')
}

function monthsRunning(startedAt: string): number {
  const start = new Date(startedAt)
  const now = new Date()
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())

  return Math.max(months, 0)
}

function daysUntil(date: Date): number {
  const diff = date.getTime() - Date.now()

  return Math.max(Math.ceil(diff / (24 * 60 * 60 * 1000)), 0)
}

function formatAmount(amount: string | null): string | null {
  if (!amount) return null
  const parsed = Number.parseFloat(amount)

  return Number.isNaN(parsed) ? null : `£${parsed.toFixed(2)}`
}

function billingLabel(subscription: Subscription): string {
  return formatFrequency(subscription) ?? '-'
}

function StatusMetric({
  label,
  primary,
  secondary,
  withDot
}: {
  label?: string
  primary: string
  secondary?: string | null
  withDot?: boolean
}) {
  return (
    <Grid size={4}>
      {label && <Typography sx={{ color: colors.text.secondary, fontSize: '12px', mb: 0.5 }}>{label}</Typography>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {withDot && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: `2px solid ${colors.text.secondary}`
            }}
          />
        )}
        <Typography sx={{ color: colors.text.dark, fontSize: '16px', fontWeight: 600 }}>{primary}</Typography>
      </Box>
      {secondary && (
        <Typography sx={{ color: colors.text.secondary, fontSize: '13px', mt: 0.5 }}>{secondary}</Typography>
      )}
    </Grid>
  )
}

function cardLabel(subscription: Subscription): string {
  if (!subscription.cardBrand && !subscription.cardLast4) return 'Card details unavailable'

  return [subscription.cardBrand, subscription.cardLast4 && `ending ${subscription.cardLast4}`]
    .filter(Boolean)
    .join(' ')
}

function SubscriptionStatusCard({ subscription }: { subscription: Subscription }) {
  const nextPaymentDate = subscription.nextPaymentDate ? new Date(subscription.nextPaymentDate.replace(' ', 'T')) : null
  const nextPaymentAmount = formatAmount(subscription.recurringAmount)
  const months = monthsRunning(subscription.startedAt)

  return (
    <Paper id='subscription-status' elevation={1} sx={{ ...sectionStyles }}>
      <Typography variant='sectionTitle' sx={{ mb: 2, display: 'block' }}>
        Subscription Status
      </Typography>

      <Box sx={{ bgcolor: colors.background.surface, borderRadius: borderRadius.lg, p: 3 }}>
        <DetailFieldRow>
          <StatusMetric
            withDot
            primary={orderStatusValueToLabel(subscription.status)}
            secondary={`Started ${formatDate(subscription.startedAt)} · running ${months} ${
              months === 1 ? 'month' : 'months'
            }`}
          />
          <StatusMetric
            label='Next payment'
            primary={nextPaymentDate ? format(nextPaymentDate, 'dd/MM/yyyy') : '-'}
            secondary={
              nextPaymentDate
                ? [nextPaymentAmount, `in ${daysUntil(nextPaymentDate)} days`].filter(Boolean).join(' · ')
                : 'Not recorded for this subscription'
            }
          />
          <StatusMetric label='Billing' primary={billingLabel(subscription)} secondary={cardLabel(subscription)} />
        </DetailFieldRow>
      </Box>
    </Paper>
  )
}

function SubscriptionDetailsCard({ subscription }: { subscription: Subscription }) {
  const patientName = subscription.patient
    ? `${subscription.patient.firstName ?? ''} ${subscription.patient.lastName ?? ''}`.trim()
    : null

  return (
    <Paper
      id='subscription-details'
      elevation={1}
      sx={{ ...sectionStyles, display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Typography variant='sectionTitle'>Subscription details</Typography>

      <DetailFieldRow>
        <DetailField label='Subscription number' value={subscription.subscriptionNumber} />
        <DetailField label='Status' value={orderStatusValueToLabel(subscription.status)} />
        <DetailField label='User name' value={subscription.user.displayName} />
        <DetailField label='Frequency' value={billingLabel(subscription)} />
      </DetailFieldRow>

      <DetailFieldRow>
        <DetailField label='Email' value={subscription.user.email} />
        <DetailField label='Mobile' value={subscription.user.phone} />
        <DetailField label='Medications' value={subscription.medicationCount} />
        <DetailField label='Orders created' value={subscription.ordersCount} />
      </DetailFieldRow>

      <DetailFieldRow>
        <DetailField label='Patient' value={patientName} />
        <DetailField label='Plan' value={subscription.planName} />
        <DetailField label='Category' value={subscription.category} />
        <DetailField label='Cancellation reason' value={subscription.cancellationReason} />
      </DetailFieldRow>
    </Paper>
  )
}

function MedicationsCard({ subscription }: { subscription: Subscription }) {
  return (
    <CollapsibleCard
      sectionId='medications'
      title='Medications'
      isEmpty={!subscription.medications.length}
      emptyMessage='No medications on this subscription yet'
    >
      <Box
        sx={{
          ...sxPresets().section.content,
          bgcolor: colors.background.surface,
          ...sxPresets().grid.threeColumn,
          ...sxPresets(160).scrollable.twoRows
        }}
      >
        {subscription.medications.map(medication => (
          <Paper key={medication.itemHash} elevation={0} sx={{ ...sxPresets().card.default, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant='smallText'>{medication.categories.join(', ') || 'Unknown'}</Typography>
              {medication.status && <MedReqStatusBadge status={medication.status} />}
            </Box>
            <Typography variant='cardTitle'>{medication.name ?? medication.productName}</Typography>
          </Paper>
        ))}
      </Box>
    </CollapsibleCard>
  )
}

function SubscriptionOrdersCard({ subscription }: { subscription: Subscription }) {
  const navigate = useNavigate()

  return (
    <CollapsibleCard
      sectionId='subscription-orders'
      title='Orders'
      isEmpty={!subscription.orders.length}
      emptyMessage='No orders found'
    >
      <Box
        sx={{
          ...sxPresets().section.content,
          bgcolor: colors.background.surface,
          ...sxPresets().grid.threeColumn,
          ...sxPresets(180).scrollable.twoRows
        }}
      >
        {subscription.orders.map(order => (
          <Paper
            key={order.hash}
            elevation={0}
            onClick={() => navigate(`/orders/${order.hash}`)}
            sx={{ ...sxPresets().card.default, ...sxPresets().card.clickable, p: 2 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant='smallText'>{subscription.category ?? 'Unknown'}</Typography>
              <OrderStatusBadge status={order.status} />
            </Box>
            <Typography variant='cardTitle' sx={{ mb: 1 }}>
              {order.orderNumber ?? 'Order'}
            </Typography>
            <OrderSubscriptionTypeChip
              subscriptionType={order.type as OrderSubscriptionType}
              orderNumber={order.orderNumber ?? ''}
              sx={{ mb: 1 }}
            />
            <Typography variant='smallText' sx={{ display: 'block' }}>
              {formatDateTime(new Date(order.createdAt))}
            </Typography>
            <Typography variant='smallText' sx={{ display: 'block' }}>
              {formatAmount(order.totalAmount) ?? '-'}
            </Typography>
          </Paper>
        ))}
      </Box>
    </CollapsibleCard>
  )
}

function SubscriptionHeader({ subscription }: { subscription: Subscription }) {
  const queryClient = useQueryClient()
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const reviewOrder = subscription.orders.find(order => order.evaroType === 'mps_med_order') ?? subscription.orders[0]
  const { data: review } = useReviewQuestionnaire(reviewOrder?.hash, !!reviewOrder)
  const sendReview = useSendReviewQuestionnaire()

  const reviewAlreadySent = review?.requestStatus === 'pending'
  const canCancel = CANCELLABLE_SUBSCRIPTION_STATUSES.includes(subscription.status) && !isReadOnly()
  const canSendReview =
    !!reviewOrder && !!review?.available && !reviewAlreadySent && !sendReview.isPending && !isReadOnly()

  const handleSendReview = async () => {
    if (!reviewOrder || isReadOnly()) return

    const result = await sendReview.mutateAsync(reviewOrder.hash)

    enqueueSnackbar(result.alreadyOpen ? 'A review is already waiting for this patient' : 'Review questionnaire sent', {
      variant: result.alreadyOpen ? 'info' : 'success'
    })

    queryClient.invalidateQueries({ queryKey: ['review-questionnaire', reviewOrder.hash] })
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <Typography variant='pageTitle' sx={{ fontSize: 24 }}>
        {subscription.planName ?? 'Subscription'}
      </Typography>
      <OrderStatusBadge status={subscription.status} />
      <TabButton active={false} onClick={handleSendReview} disabled={!canSendReview}>
        {reviewAlreadySent ? 'Review already sent' : 'Request review'}
      </TabButton>
      <TabButton active={false} onClick={() => setCancelDialogOpen(true)} disabled={!canCancel}>
        Cancel subscription
      </TabButton>
      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        subscription={subscription}
        onClose={() => setCancelDialogOpen(false)}
      />
    </Box>
  )
}

export default function SubscriptionDetailPage() {
  const { hash } = useParams<{ hash: string }>()
  const location = useLocation() as { state?: { fallbackPath?: string; backLabel?: string } }

  if (!hash) {
    throw new Error('Subscription hash is required for subscription display')
  }

  const { data: subscription, isLoading } = useSubscriptionByHashQuery(hash)

  if (isLoading || !subscription) {
    return (
      <Box sx={{ p: 4 }}>
        <InlineLoadingSpinner text='Loading subscription...' />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      <BackButton
        fallbackPath={location.state?.fallbackPath ?? '/users'}
        defaultLabel={location.state?.backLabel ?? 'Back to users list'}
      />

      <SubscriptionHeader subscription={subscription} />

      <Grid container spacing={4}>
        <Grid size={9} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <SubscriptionStatusCard subscription={subscription} />
          <SubscriptionDetailsCard subscription={subscription} />
          <MedicationsCard subscription={subscription} />
          <SubscriptionOrdersCard subscription={subscription} />
        </Grid>
        <Grid size={3} sx={{ position: 'sticky', top: 88, alignSelf: 'flex-start' }}>
          <NavMenu items={navItems} onNavigate={scrollToSection} />
        </Grid>
      </Grid>
    </Box>
  )
}
